// @flow

import type {
  CarouselPreference,
  CarouselIdType,
  TimerPreferences,
  TimerPreferenceType,
} from './types';

export const changeEnabledStatus = (
  carousels: Array<CarouselPreference>,
  id: CarouselIdType,
  enabled: boolean,
): Array<CarouselPreference> =>
  carousels.map((pref: CarouselPreference) => (pref.id === id ? { ...pref, enabled } : pref));

export const reorderCarousel = (
  carousels: Array<CarouselPreference>,
  fromIndex: number,
  toIndex: number,
): Array<CarouselPreference> | false => {
  if (fromIndex === toIndex) {
    /* Nothing has changed - no need to alter initial carousel preferences */
    return false;
  }

  carousels = [...carousels]; /* make a copy to prevent mutations */
  const maxAllowedIndex = carousels.length - 1;

  if (fromIndex < 0 || fromIndex > maxAllowedIndex || toIndex < 0 || toIndex > maxAllowedIndex) {
    /* Out of bounds */
    return false;
  }

  const itemToMove = carousels[fromIndex];

  carousels.splice(fromIndex, 1); /* remove 1 element (moved) */
  carousels.splice(toIndex, 0, itemToMove); /* remove 0 elements and insert moved item back */

  /* Update sort field (to reflect new order of items) */
  const withUpdatedSort = carousels.map((pref: CarouselPreference, index: number) => ({
    ...pref,
    sort: index,
  }));
  return withUpdatedSort;
};

export const carouselsToHashMap = (
  carousels: Array<CarouselPreference>,
): { [key: string]: CarouselPreference } =>
  carousels.reduce(
    (
      acc: { [key: string]: CarouselPreference },
      carouselPref: CarouselPreference,
      index: number,
    ) => {
      acc[index.toString()] = carouselPref;
      return acc;
    },
    {},
  );

export const updateTimerPreferences = (
  preferences: TimerPreferences,
  preferenceTypeToUpdate: TimerPreferenceType,
  value: string,
): {| updatedPreferences: TimerPreferences, valueBeforeUpdate: string |} => {
  let updatedPreferences = preferences;
  let valueBeforeUpdate = '';

  switch (preferenceTypeToUpdate) {
    case 'START_EARLY':
      valueBeforeUpdate = preferences.startEarly;
      updatedPreferences = { ...updatedPreferences, startEarly: value };
      break;

    case 'MAX_RECORDINGS':
      valueBeforeUpdate = preferences.maxRecordings;
      updatedPreferences = { ...updatedPreferences, maxRecordings: value };
      break;

    case 'END_LATE':
      valueBeforeUpdate = preferences.endLate;
      updatedPreferences = { ...updatedPreferences, endLate: value };
      break;

    case 'PRIORITY':
      valueBeforeUpdate = preferences.priority;
      updatedPreferences = { ...updatedPreferences, priority: value };
      break;

    case 'KEEP':
      valueBeforeUpdate = preferences.keep;
      updatedPreferences = { ...updatedPreferences, keep: value };
      break;

    default: {
      (preferenceTypeToUpdate: empty); /* eslint-disable-line */
    }
  }
  return { updatedPreferences, valueBeforeUpdate };
};

export const getListOfSortedCarouselIds = (
  carousels: Array<CarouselPreference>,
): Array<CarouselIdType> => {
  const copy = [...carousels];
  copy.sort((carouselA, carouselB) =>
    carouselA.id.toLowerCase() > carouselB.id.toLowerCase() ? 1 : -1,
  );
  return copy.map(({ id }) => id);
};
